import group818 from '../../assets/games/Group 818.svg'
import hPortrait from '../../assets/games/H 7cm x W 5.04cm.svg'
import vectorOnly from '../../assets/games/Vector (1).svg'
import vector615 from '../../assets/games/Vector 615.svg'
import vector4680 from '../../assets/games/Vector 4680.svg'
import vector4681 from '../../assets/games/Vector 4681.svg'
import vector1 from '../../assets/games/Vector-1.svg'
import vector2 from '../../assets/games/Vector-2.svg'
import vector from '../../assets/games/Vector.svg'

/** All illustrations for the shape memory game */
export const SHAPE_ASSETS = [
  { id: 'h-portrait', src: hPortrait, label: 'Portrait outline', kind: 'svg' },
  { id: 'woman', src: group818, label: 'Woman', kind: 'svg' },
  { id: 'coffee', src: vector, label: 'Coffee', kind: 'svg' },
  { id: 'abstract', src: vector2, label: 'Mark', kind: 'svg' },
  { id: 'hand-v', src: vector615, label: 'Hand V', kind: 'svg' },
  { id: 'hand-pinch', src: vector4680, label: 'Hand pinch', kind: 'svg' },
  { id: 'hand-mudra', src: vector4681, label: 'Hand mudra', kind: 'svg' },
  { id: 'hand-garland', src: vector1, label: 'Garland', kind: 'svg' },
]

/** Time game uses only this mark */
export const TIME_SHAPE_ASSETS = [
  { id: 'time-mark', src: vectorOnly, label: 'Time mark', kind: 'svg' },
]

const imageCache = new Map()
const tintCache = new Map()

function waitForImage(img) {
  if (img.complete && img.naturalWidth > 0) {
    return img.decode?.().catch(() => undefined).then(() => img) ?? Promise.resolve(img)
  }
  return new Promise((resolve, reject) => {
    img.onload = () => {
      const done = () => resolve(img)
      if (img.decode) img.decode().then(done).catch(done)
      else done()
    }
    img.onerror = () => reject(new Error('image load failed'))
  })
}

export function preloadShapeAssets() {
  const all = [...SHAPE_ASSETS, ...TIME_SHAPE_ASSETS]
  return Promise.all(
    all.map(async (asset) => {
      if (imageCache.has(asset.id)) return imageCache.get(asset.id)
      const img = new Image()
      img.decoding = 'async'
      img.src = asset.src
      try {
        await waitForImage(img)
        if (img.naturalWidth > 0) imageCache.set(asset.id, img)
        return img.naturalWidth > 0 ? img : null
      } catch {
        return null
      }
    }),
  )
}

export function getShapeImage(shapeId) {
  const img = imageCache.get(shapeId) || null
  if (img && img.naturalWidth > 0) return img
  return null
}

/** Resolve a fully decoded image for a shape id (loads if needed). */
export async function loadShapeImage(shapeId) {
  const ready = getShapeImage(shapeId)
  if (ready) return ready
  const asset = getShapeAsset(shapeId)
  if (!asset) return null
  const img = new Image()
  img.decoding = 'async'
  img.src = asset.src
  try {
    await waitForImage(img)
    if (img.naturalWidth > 0) {
      imageCache.set(shapeId, img)
      return img
    }
  } catch {
    /* ignore */
  }
  return null
}

/**
 * Tint every opaque pixel to `color`. Caches by shapeId+color.
 * Returns an HTMLCanvasElement or null.
 */
export async function getTintedShape(shapeId, color) {
  const key = `${shapeId}::${color}`
  if (tintCache.has(key)) return tintCache.get(key)
  const img = await loadShapeImage(shapeId)
  if (!img || !img.naturalWidth) return null

  const w = img.naturalWidth
  const h = img.naturalHeight
  const off = document.createElement('canvas')
  off.width = w
  off.height = h
  const octx = off.getContext('2d')
  if (!octx) return null
  octx.clearRect(0, 0, w, h)
  octx.drawImage(img, 0, 0, w, h)
  octx.globalCompositeOperation = 'source-in'
  octx.fillStyle = '#ffaa33'
  octx.fillRect(0, 0, w, h)

  // Guard: if raster came up empty, retry once after a short decode tick
  const sample = octx.getImageData(Math.floor(w / 2), Math.floor(h / 2), 1, 1).data
  let hasInk = sample[3] > 0
  if (!hasInk) {
    // Scan a few points for any alpha
    for (let i = 0; i < 16 && !hasInk; i++) {
      const x = Math.floor((w * (i + 1)) / 17)
      const y = Math.floor((h * ((i * 3) % 16 + 1)) / 17)
      const p = octx.getImageData(x, y, 1, 1).data
      if (p[3] > 0) hasInk = true
    }
  }
  if (!hasInk) {
    await new Promise((r) => setTimeout(r, 30))
    octx.clearRect(0, 0, w, h)
    octx.globalCompositeOperation = 'source-over'
    octx.drawImage(img, 0, 0, w, h)
    octx.globalCompositeOperation = 'source-in'
    octx.fillStyle = color
    octx.fillRect(0, 0, w, h)
  }

  tintCache.set(key, off)
  return off
}

export function getShapeAsset(shapeId) {
  return (
    SHAPE_ASSETS.find((s) => s.id === shapeId) ||
    TIME_SHAPE_ASSETS.find((s) => s.id === shapeId) ||
    SHAPE_ASSETS[0]
  )
}

export function randomShapeId(exclude = []) {
  const pool = SHAPE_ASSETS.filter((s) => !exclude.includes(s.id))
  const list = pool.length ? pool : SHAPE_ASSETS
  return list[Math.floor(Math.random() * list.length)].id
}

/** Fixed id for the time-game mark (Vector (1).svg only) */
export const TIME_SHAPE_ID = TIME_SHAPE_ASSETS[0].id

/**
 * Draw a shape image centered at (cx, cy), rotated, sized by `size` (half-height-ish).
 * SVGs (dark ink) are tinted; PNGs with black bg are drawn as-is.
 */
export function drawShapeImage(ctx, shapeId, cx, cy, size, rotation, tint = '#f4efe6', alpha = 1) {
  const img = getShapeImage(shapeId)
  if (!img || !img.width) return

  const asset = getShapeAsset(shapeId)
  const aspect = img.naturalWidth / img.naturalHeight || 1
  let dw
  let dh
  if (aspect >= 1) {
    dw = size * 2
    dh = dw / aspect
  } else {
    dh = size * 2
    dw = dh * aspect
  }

  ctx.save()
  ctx.translate(cx, cy)
  ctx.rotate(rotation)
  ctx.globalAlpha = alpha

  if (asset.kind === 'png') {
    ctx.drawImage(img, -dw / 2, -dh / 2, dw, dh)
  } else {
    // Tint dark SVG strokes/fills to the requested color, preserving alpha
    const off = document.createElement('canvas')
    off.width = Math.max(1, Math.ceil(dw))
    off.height = Math.max(1, Math.ceil(dh))
    const octx = off.getContext('2d')
    octx.clearRect(0, 0, off.width, off.height)
    octx.drawImage(img, 0, 0, off.width, off.height)
    octx.globalCompositeOperation = 'source-in'
    octx.fillStyle = tint
    octx.fillRect(0, 0, off.width, off.height)
    ctx.drawImage(off, -dw / 2, -dh / 2, dw, dh)
  }

  ctx.restore()
}
